import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "image-attachment__wrapper" }
const _hoisted_2 = {
  key: 0,
  class: "image-attachment__progress-container"
}
const _hoisted_3 = {
  key: 1,
  class: "image-attachment__icon-container"
}
const _hoisted_4 = ["src", "alt"]
const _hoisted_5 = {
  key: 2,
  class: "image-attachment__error"
}
const _hoisted_6 = {
  key: 3,
  class: "image-attachment__badge image-attachment__badge--sticker"
}

import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import type { Attachment } from "@/dtos/Unipile.dto";
import { Icon } from "@iconify/vue";
import { useAttachmentLoader } from "@/composables/attachments/useAttachmentLoader";
import ProgressCircle from "./ProgressCircle.vue";

interface Props {
  attachment: Attachment;
  messageId?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ImageAttachment',
  props: {
    attachment: {},
    messageId: {}
  },
  emits: ["imageClick"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const containerRef = ref<HTMLElement | null>(null);
const imageError = ref(false);
const imageLoaded = ref(false);
let intersectionObserver: IntersectionObserver | null = null;

const { isLoading, loadingProgress, blobUrl, loadAttachment, getAttachmentUrl } =
  useAttachmentLoader(props.attachment, props.messageId, () => {});

const resolvedImageUrl = computed(() => {
  if (blobUrl.value) return blobUrl.value;
  return getAttachmentUrl(props.attachment.url);
});

const loadImage = async () => {
  if (!props.attachment.url) return;

  if (props.attachment.url.startsWith("att://") && props.messageId && props.attachment.id) {
    const blob = await loadAttachment("image/jpeg");
    if (blob) {
      imageError.value = false;
      imageLoaded.value = true;
    } else {
      imageError.value = true;
      imageLoaded.value = false;
    }
  } else {
    imageLoaded.value = true;
  }
};

const handleClick = async () => {
  let url = resolvedImageUrl.value;

  if (
    !blobUrl.value &&
    props.attachment.url?.startsWith("att://") &&
    props.messageId &&
    props.attachment.id
  ) {
    const blob = await loadAttachment("image/jpeg");
    if (blob) {
      url = blobUrl.value!;
    }
  }

  if (url) {
    emit("imageClick", url, props.attachment);
  }
};

const handleError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  if (img && img.src && img.src !== resolvedImageUrl.value) {
    return;
  }
  imageError.value = true;
  imageLoaded.value = false;
};

const handleLoad = () => {
  imageError.value = false;
};

const setupLazyLoading = () => {
  if (
    props.attachment.type === "img" &&
    props.attachment.url?.startsWith("att://") &&
    props.messageId &&
    props.attachment.id &&
    !imageLoaded.value &&
    !isLoading.value
  ) {
    if ("IntersectionObserver" in window && containerRef.value) {
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !imageLoaded.value && !isLoading.value) {
              loadImage();
              if (intersectionObserver && containerRef.value) {
                intersectionObserver.unobserve(containerRef.value);
              }
            }
          });
        },
        { rootMargin: "50px" }
      );
      intersectionObserver.observe(containerRef.value);
    } else {
      loadImage();
    }
  }
};

watch(
  () => [props.attachment.id, props.messageId, props.attachment.url],
  () => {
    if (props.attachment.type === "img" && !props.attachment.url?.startsWith("att://")) {
      imageError.value = false;
      imageLoaded.value = true;
    }
  },
  { immediate: false }
);

watch(
  () => resolvedImageUrl.value,
  (url) => {
    if (url && !props.attachment.url?.startsWith("att://")) {
      imageLoaded.value = true;
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (props.attachment.type === "img" && !props.attachment.url?.startsWith("att://")) {
    imageLoaded.value = true;
  } else if (props.attachment.type === "img" && props.attachment.url?.startsWith("att://")) {
    setTimeout(() => {
      setupLazyLoading();
    }, 100);
  }
});

onUnmounted(() => {
  if (intersectionObserver && containerRef.value) {
    intersectionObserver.unobserve(containerRef.value);
    intersectionObserver.disconnect();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "image-attachment",
    ref_key: "containerRef",
    ref: containerRef
  }, [
    _createElementVNode("div", _hoisted_1, [
      (!imageLoaded.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "image-attachment__placeholder",
            onClick: loadImage
          }, [
            (_unref(isLoading))
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createVNode(ProgressCircle, {
                    progress: _unref(loadingProgress),
                    size: "medium",
                    "bg-stroke": "rgba(255, 255, 255, 0.2)",
                    "fill-stroke": "white"
                  }, null, 8, ["progress"])
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createVNode(_unref(Icon), {
                    icon: "majesticons:arrow-down",
                    class: "image-attachment__preview-icon"
                  })
                ])),
            _createElementVNode("span", null, _toDisplayString(_unref(isLoading) ? "Loading..." : "Click to preview"), 1)
          ]))
        : _createCommentVNode("", true),
      (imageLoaded.value && resolvedImageUrl.value)
        ? (_openBlock(), _createElementBlock("img", {
            key: 1,
            src: resolvedImageUrl.value,
            alt: __props.attachment.name || 'Image',
            class: "image-attachment__img",
            onError: handleError,
            onClick: handleClick,
            onLoad: handleLoad
          }, null, 40, _hoisted_4))
        : _createCommentVNode("", true),
      (imageError.value && imageLoaded.value && !resolvedImageUrl.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createVNode(_unref(Icon), { icon: "mdi:image-off" }),
            _cache[0] || (_cache[0] = _createElementVNode("span", null, "Failed to load image", -1))
          ]))
        : _createCommentVNode("", true),
      (__props.attachment.sticker)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createVNode(_unref(Icon), { icon: "mdi:emoticon" }),
            _cache[1] || (_cache[1] = _createElementVNode("span", null, "Sticker", -1))
          ]))
        : _createCommentVNode("", true)
    ])
  ], 512))
}
}

})